import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  Home,
  MessageCircle,
  PlusSquare,
  Search,
  Settings,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'
import { Suspense, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigation } from 'react-router'

import { authQueries } from '@/features/auth/api/auth.queries'
import { useAuth } from '@/features/auth/session/auth-context'
import { messagingQueries } from '@/features/messaging/api/messaging.queries'
import { notificationQueries } from '@/features/notifications/api/notifications.queries'
import { profileApi } from '@/features/profile/api/profile.api'
import { SocialSanityIntroModal } from '@/features/feed/components/SocialSanityIntroModal'
import { markSocialSanityIntroSeen, socialSanityIntroSeen } from '@/features/feed/lib/social-sanity'
import { CountryPickerModal } from '@/features/settings/components/CountryPickerModal'
import { CountryStatusModal } from '@/features/settings/components/CountryStatusModal'
import { Avatar } from '@/shared/components/Avatar'
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher'
import { PageLoader } from '@/shared/components/PageLoader'
import { env } from '@/shared/config/env'
import { elevaLinks } from '@/shared/config/links'
import { useTheme } from '@/shared/theme/theme-context'

/**
 * The landing page's theme artwork, reused as a soft app-wide backdrop:
 * fixed behind everything at 30% opacity, following the active theme.
 */
function ThemedBackdrop() {
  const { theme } = useTheme()
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <img
        src={`/assets/img/backgrounds/${theme}.jpg`}
        alt=""
        decoding="async"
        draggable={false}
        className="size-full object-cover opacity-30"
      />
    </div>
  )
}

export function RootLayout() {
  const { isAuthenticated } = useAuth()
  const navigation = useNavigation()

  return (
    <>
      {/* Thin progress bar while a lazy route chunk is being fetched. */}
      {navigation.state !== 'idle' && (
        <div className="bg-primary fixed inset-x-0 top-0 z-[70] h-0.5 animate-pulse" />
      )}
      {isAuthenticated ? <AppShell /> : <PublicShell />}
      <ScrollRestoration />
    </>
  )
}

/** Minimal centered chrome for the signed-out pages (login, register, …). */
function PublicShell() {
  const { t } = useTranslation('common')
  // The landing page brings its own full-bleed sections and rich footer,
  // so the centered auth chrome steps aside for it.
  const isLanding = useLocation().pathname === '/welcome'

  return (
    <div className="flex min-h-dvh flex-col">
      {!isLanding && <ThemedBackdrop />}
      <header className="flex items-center justify-between px-6 py-4">
        <Logo />
        <div className="flex items-center gap-2">
          {isLanding ? (
            /* Hidden on phones - the hero right below carries the same CTA. */
            <Link
              to="/login"
              className="bg-primary hover:bg-primary-hover hidden rounded-full px-4 py-1.5 text-sm font-bold text-white transition sm:inline-flex"
            >
              {t('nav.signIn')}
            </Link>
          ) : (
            <Link
              to="/welcome"
              className="text-fg-muted hover:text-fg px-2 text-sm font-medium transition"
            >
              {t('nav.about')}
            </Link>
          )}
          <LanguageSwitcher />
        </div>
      </header>
      <main className={isLanding ? 'flex-1' : 'flex flex-1 flex-col justify-center px-4 pb-16'}>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
      {!isLanding && (
        <footer className="text-fg-subtle space-y-2 px-6 py-4 text-center text-xs">
          <p>{t('footer.builtWith')}</p>
          {/* Same agreement links the mobile apps show before sign-in. */}
          <p className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/welcome" className="hover:text-fg transition">
              {t('footer.about')}
            </Link>
            <FooterStaticLink href={elevaLinks.legal.termsOfService}>
              {t('footer.terms')}
            </FooterStaticLink>
            <FooterStaticLink href={elevaLinks.legal.privacyPolicy}>
              {t('footer.privacy')}
            </FooterStaticLink>
            <FooterStaticLink href={elevaLinks.legal.communityGuidelines}>
              {t('footer.guidelines')}
            </FooterStaticLink>
          </p>
        </footer>
      )}
    </div>
  )
}

function FooterStaticLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className="hover:text-fg transition">
      {children}
    </a>
  )
}

/**
 * Signed-in chrome: fixed sidebar on desktop, bottom tab bar on mobile -
 * the web counterpart of the iOS home-button navigation arc.
 */
function AppShell() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const messagesUnread = useQuery(messagingQueries.unreadCount())
  const notificationsUnread = useQuery(notificationQueries.unreadCount())

  // Accounts without a country (registered before the country feature, or
  // nothing was detectable at register time) get the picker once per session.
  const me = useQuery(authQueries.me())
  const [countryGateDismissed, setCountryGateDismissed] = useState(false)
  // Country whose release-status popup is showing (set after the gate saves).
  const [statusCountry, setStatusCountry] = useState<string | null>(null)
  const saveCountry = useMutation({
    mutationFn: profileApi.updateCountry,
    onSuccess: (_data, code) => {
      setStatusCountry(code)
      void queryClient.invalidateQueries({ queryKey: authQueries.me().queryKey })
    },
  })
  const countryGateOpen =
    me.isSuccess && me.data.countryCode === null && !countryGateDismissed && !saveCountry.isPending
  // One-time social sanity explainer at app start; it waits for the country
  // gate so two popups never stack.
  const [sanityIntroOpen, setSanityIntroOpen] = useState(() => !socialSanityIntroSeen())

  const profilePath = user ? `/profile/${user.userId}` : '/settings'

  return (
    <div className="min-h-dvh lg:pl-64">
      <ThemedBackdrop />
      {/* Desktop sidebar */}
      <aside className="border-border bg-surface fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r lg:flex">
        <div className="flex items-center gap-2 px-6 pt-6 pb-4">
          <Logo />
        </div>

        <nav className="flex flex-1 flex-col gap-1 px-3 py-2">
          <SideLink to="/" icon={Home} label={t('nav.feed')} end />
          <SideLink to="/search" icon={Search} label={t('nav.search')} />
          <SideLink to="/communities" icon={UsersRound} label={t('nav.communities')} />
          <SideLink
            to="/messages"
            icon={MessageCircle}
            label={t('nav.messages')}
            badge={messagesUnread.data ?? 0}
          />
          <SideLink
            to="/notifications"
            icon={Bell}
            label={t('nav.notifications')}
            badge={notificationsUnread.data ?? 0}
          />
          <SideLink to="/create" icon={PlusSquare} label={t('nav.create')} accent />

          <div className="flex-1" />

          <SideLink to="/settings" icon={Settings} label={t('nav.settings')} />
          <NavLink
            to={profilePath}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 transition ${
                isActive ? 'bg-primary-soft text-fg' : 'text-fg-muted hover:bg-surface-hover'
              }`
            }
          >
            <Avatar src={user?.avatarUrl} size={30} />
            <span className="min-w-0">
              <span className="text-fg block truncate text-sm font-semibold">
                {user?.displayName ?? user?.username}
              </span>
              <span className="text-fg-subtle block truncate text-xs">@{user?.username}</span>
            </span>
          </NavLink>
        </nav>

        <div className="border-border flex items-center justify-between border-t px-4 py-3">
          <LanguageSwitcher />
          {env.appEnv !== 'prod' && <EnvBadge />}
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="border-border bg-surface/95 sticky top-0 z-40 flex items-center justify-between border-b px-4 py-2.5 backdrop-blur lg:hidden">
        <Logo />
        <div className="flex items-center gap-1">
          {env.appEnv !== 'prod' && <EnvBadge />}
          <TopIconLink to="/communities" icon={UsersRound} label={t('nav.communities')} />
          <TopIconLink
            to="/notifications"
            icon={Bell}
            label={t('nav.notifications')}
            badge={notificationsUnread.data ?? 0}
          />
          <TopIconLink to="/settings" icon={Settings} label={t('nav.settings')} />
        </div>
      </header>

      <main className="pb-20 lg:pb-0">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="border-border bg-surface/95 fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t backdrop-blur lg:hidden">
        <TabLink to="/" icon={Home} label={t('nav.feed')} end />
        <TabLink to="/search" icon={Search} label={t('nav.search')} />
        <TabLink to="/create" icon={PlusSquare} label={t('nav.create')} accent />
        <TabLink
          to="/messages"
          icon={MessageCircle}
          label={t('nav.messages')}
          badge={messagesUnread.data ?? 0}
        />
        <NavLink
          to={profilePath}
          className="text-fg-muted flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium"
        >
          <Avatar src={user?.avatarUrl} size={22} />
          {t('nav.profile')}
        </NavLink>
      </nav>

      <CountryPickerModal
        open={countryGateOpen}
        selected={null}
        onSelect={(code) => saveCountry.mutate(code)}
        onClose={() => setCountryGateDismissed(true)}
      />

      <CountryStatusModal code={statusCountry} onClose={() => setStatusCountry(null)} />

      <SocialSanityIntroModal
        open={sanityIntroOpen && !countryGateOpen && statusCountry === null}
        onClose={() => {
          markSocialSanityIntroSeen()
          setSanityIntroOpen(false)
        }}
      />
    </div>
  )
}

function Logo() {
  const { t } = useTranslation('common')
  return (
    <NavLink to="/" className="flex items-center gap-2">
      <span className="from-primary to-secondary flex size-8 items-center justify-center rounded-xl bg-gradient-to-br text-sm font-black text-white">
        E
      </span>
      <span className="text-fg text-lg font-bold tracking-tight">{t('app.name')}</span>
    </NavLink>
  )
}

function EnvBadge() {
  return (
    <span className="bg-primary-soft text-secondary rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase">
      {env.appEnv}
    </span>
  )
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <span className="bg-primary absolute -top-1.5 -right-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white">
      {Math.min(count, 99)}
    </span>
  )
}

interface LinkProps {
  to: string
  icon: LucideIcon
  label: string
  badge?: number
  accent?: boolean
  end?: boolean
}

function SideLink({ to, icon: Icon, label, badge = 0, accent = false, end = false }: LinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
          isActive
            ? 'bg-primary-soft text-fg'
            : accent
              ? 'text-primary hover:bg-surface-hover'
              : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
        }`
      }
    >
      <span className="relative">
        <Icon className="size-5" />
        <UnreadBadge count={badge} />
      </span>
      {label}
    </NavLink>
  )
}

function TabLink({ to, icon: Icon, label, badge = 0, accent = false, end = false }: LinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition ${
          isActive ? 'text-primary' : accent ? 'text-primary' : 'text-fg-muted'
        }`
      }
    >
      <span className="relative">
        <Icon className="size-[22px]" />
        <UnreadBadge count={badge} />
      </span>
      {label}
    </NavLink>
  )
}

function TopIconLink({
  to,
  icon: Icon,
  label,
  badge = 0,
}: {
  to: string
  icon: LucideIcon
  label: string
  badge?: number
}) {
  return (
    <NavLink
      to={to}
      aria-label={label}
      className="text-fg-muted hover:text-fg relative rounded-lg p-2 transition"
    >
      <Icon className="size-5" />
      <UnreadBadge count={badge} />
    </NavLink>
  )
}

/** Standard centered page container for content routes. */
export function PageContainer({
  children,
  width = 'md',
}: {
  children: ReactNode
  width?: 'sm' | 'md' | 'lg'
}) {
  const widthClass = { sm: 'max-w-xl', md: 'max-w-2xl', lg: 'max-w-5xl' }[width]
  return <div className={`mx-auto w-full ${widthClass} px-4 py-6`}>{children}</div>
}
